import styled from "@emotion/styled";
import SkapaText, {
  type TextAsLabelTagProps,
  type TextAsNonLabelTagProps,
  type TextAttributes,
  type TextProps,
} from "@ingka/text";
import Variables from "@ingka/variables";

type TextTagProps = TextAsLabelTagProps | TextAsNonLabelTagProps;

type Props = {
  bold?: boolean;
  thin?: boolean;
  color?: string;
  noWrap?: boolean;
  overflowHidden?: boolean;
  wordBreakWord?: boolean;
  pre?: boolean;
  ellipsis?: boolean;
  rows?: number;
  xsmall?: boolean;
  tagName?: TextTagProps["tagName"];
  lineThrough?: boolean;
  inline?: boolean;
  italic?: boolean;
  flexGrow?: number;
} & TextProps &
  TextTagProps &
  TextAttributes;

const StyledText = styled(SkapaText, {
  shouldForwardProp: (prop) =>
    ![
      "bold",
      "thin",
      "color",
      "noWrap",
      "overflowHidden",
      "wordBreakWord",
      "pre",
      "xsmall",
      "ellipsis",
      "lineThrough",
      "inline",
      "italic",
      "flexGrow",
      "rows",
    ].includes(prop),
})<Props>`
  && {
    ${(props) => props.bold && `font-weight: ${Variables.fontWeightBold};`};
    ${(props) => props.thin && `font-weight: ${Variables.fontWeightRegular};`};
    ${(props) => props.color && `color: ${props.color};`};
    ${(props) => props.noWrap && "white-space: nowrap;"};
    ${(props) =>
      props.overflowHidden &&
      `overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;`};
    ${(props) => props.wordBreakWord && "word-break: break-word;"};
    ${(props) => props.pre && "white-space: pre-wrap;"};
    ${(props) => props.xsmall && `font-size: ${Variables.fontSize20};`};
    ${(props) =>
      props.ellipsis &&
      `white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;`};
    ${(props) => props.lineThrough && "text-decoration: line-through;"};
    ${(props) => props.inline && "display: inline;"};
    ${(props) => props.italic && "font-style: italic;"};
    ${(props) => props.flexGrow && `flex-grow: ${props.flexGrow};`};
    ${(props) =>
      props.rows &&
      `display: -webkit-box;
        -webkit-line-clamp: ${props.rows};
        -webkit-box-orient: vertical;
        overflow: hidden;;`};
  }
`;

const Text = ({
  bold,
  thin,
  color,
  noWrap,
  overflowHidden,
  wordBreakWord,
  pre,
  rows,
  ellipsis,
  xsmall,
  lineThrough,
  inline = false,
  italic,
  flexGrow,
  ...rest
}: Props) => {
  return (
    <StyledText
      prefix="saleslead__"
      bold={bold}
      thin={thin}
      color={color}
      noWrap={noWrap}
      overflowHidden={overflowHidden}
      wordBreakWord={wordBreakWord}
      pre={pre}
      rows={rows}
      ellipsis={ellipsis}
      xsmall={xsmall}
      lineThrough={lineThrough}
      inline={inline}
      italic={italic}
      flexGrow={flexGrow}
      {...rest}
    />
  );
};

export { Text, type Props as TextProps };
